import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Building2, 
  ChartLine, 
  Shield, 
  Clock, 
  CheckCircle2, 
  ArrowRight,
  FileText,
  Users,
  Wallet,
  TrendingUp,
  ClipboardList,
  Upload,
  Brain,
  Banknote,
  Zap,
  Eye,
  Settings,
  Smartphone,
  CreditCard,
  Receipt,
  Activity
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";
const HERO_IMAGE = "https://images.unsplash.com/photo-1753185234794-e3b41b94a352?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjB3b3JraW5nfGVufDB8fHx8MTc2NzE1MDU5MHww&ixlib=rb-4.1.0&q=85";

const LandingPage = () => {
  const features = [
    {
      icon: Zap,
      title: "Fast Decisions",
      description: "Get loan decisions within 24-48 hours with our AI-powered assessment"
    },
    {
      icon: Eye,
      title: "Fair & Transparent",
      description: "Clear rates, no hidden fees, and straightforward terms you can trust"
    },
    {
      icon: Settings,
      title: "Flexible Terms",
      description: "Customized loan terms tailored to your unique business needs"
    },
    {
      icon: Clock,
      title: "Quick Application",
      description: "Complete your application in under 10 minutes online"
    },
    {
      icon: Users,
      title: "Dedicated Support",
      description: "Expert guidance and personalized support at every step"
    },
    {
      icon: TrendingUp,
      title: "Growth Focus",
      description: "Financing solutions designed to help your business scale"
    }
  ];

  const alternativeDataTypes = [
    {
      icon: FileText,
      title: "Bank Statement",
      description: "Traditional bank account statements showing cash flow and balances"
    },
    {
      icon: Smartphone,
      title: "Mobile Money Statement",
      description: "Mobile money transaction records and account activity"
    },
    {
      icon: CreditCard,
      title: "Transaction History",
      description: "Payment and sales transaction records from your business"
    },
    {
      icon: Activity,
      title: "Utility Business Performance",
      description: "Utility payments and business performance metrics"
    }
  ];

  const howItWorks = [
    {
      step: 1,
      icon: ClipboardList,
      title: "Loan Application",
      description: "Complete our simple online application in under 10 minutes with basic business details"
    },
    {
      step: 2,
      icon: Upload,
      title: "Document Upload",
      description: "Upload your business documents and alternative data to strengthen your application"
    },
    {
      step: 3,
      icon: Brain,
      title: "Decisioning",
      description: "Our AI-powered system analyzes your data and provides a decision within 24-48 hours"
    },
    {
      step: 4,
      icon: Banknote,
      title: "Receive Funding",
      description: "Once approved, funds are deposited directly into your business account"
    }
  ];

  const loanTypes = [
    { name: "Working Capital", amount: "$1K - $50K" },
    { name: "Invoice Discounting", amount: "$1K - $100K" },
    { name: "Merchant Cash Advance", amount: "$1K - $50K" },
    { name: "Term Loans", amount: "$500 - $50K" }
  ];

  const stats = [
    { value: "$1M+", label: "Funded to Date" },
    { value: "100+", label: "Businesses Helped" },
    { value: "24hrs", label: "Average Approval" },
    { value: "98%", label: "Customer Satisfaction" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-100">
                <img 
                  src={LOGO_URL} 
                  alt="Tzur Finances" 
                  className="h-8 object-contain" 
                />
              </div>
            </Link>
            
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/products" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium" data-testid="nav-products">
                Business Loans
              </Link>
              <Link to="/wholesale-lending" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium" data-testid="nav-wholesale">
                Microfinance Wholesale Loans
              </Link>
              <Link to="/individual-loans" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium" data-testid="nav-individual">
                Individual Loans
              </Link>
              <Link to="/device-finance" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium" data-testid="nav-device">
                Device Finance
              </Link>
            </nav>
            
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-[#0A2540] hover:bg-gray-100" data-testid="login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90" data-testid="get-started-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0A2540] to-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
                Fuel Your Business
                <span className="block text-amber-400 mt-2">Growth</span>
              </h1>
              <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-lg">
                Access the capital your small business needs with our innovative lending solutions. 
                We use alternative data to give you fair, fast financing.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-amber-500 text-[#0A2540] hover:bg-amber-400 h-12 px-8 font-semibold" data-testid="hero-apply-btn">
                    Apply Now <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-12 px-8" data-testid="hero-products-btn">
                    View Loan Products
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="hidden lg:block animate-fadeIn stagger-2">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-500/20 rounded-2xl transform rotate-3"></div>
                <img 
                  src={HERO_IMAGE} 
                  alt="Small business owner" 
                  className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-fadeIn" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="text-3xl md:text-4xl font-bold text-[#0A2540] font-mono">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] tracking-tight">
              Why Choose Us?
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              We're not your traditional lender. We understand small businesses and use innovative methods to assess your true potential.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 text-center">
                <div className="w-10 h-10 rounded-lg bg-[#0A2540]/10 flex items-center justify-center mb-3 mx-auto">
                  <feature.icon className="h-5 w-5 text-[#0A2540]" />
                </div>
                <h3 className="font-semibold text-sm text-[#0A2540]">{feature.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alternative Data Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] tracking-tight">
              Types of Alternative Data We Accept
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              We look beyond traditional credit scores. Submit alternative data to strengthen your application.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {alternativeDataTypes.map((item, index) => (
              <div key={index} className="text-center p-5 rounded-xl bg-gray-50 hover:bg-[#0A2540] group transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-[#0A2540] group-hover:bg-amber-500 flex items-center justify-center mx-auto mb-3 transition-colors">
                  <item.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-sm text-[#0A2540] group-hover:text-white transition-colors">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] tracking-tight">
              How It Works
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              Get funded in four simple steps.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative text-center">
                {/* Connector Line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-full h-0.5 bg-gradient-to-r from-amber-400 to-amber-200" />
                )}
                
                <div className="relative inline-block mb-3">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0A2540] to-[#1e3a8a] flex items-center justify-center mx-auto shadow-lg">
                    <item.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center text-[#0A2540] font-bold text-xs shadow-md">
                    {item.step}
                  </div>
                </div>
                
                <h3 className="font-semibold text-sm text-[#0A2540]">{item.title}</h3>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/register">
              <Button size="lg" className="bg-amber-500 text-[#0A2540] hover:bg-amber-400 h-11 px-6 font-semibold" data-testid="how-it-works-apply-btn">
                Start Your Application <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Loan Products Preview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] tracking-tight">
              Financing Solutions for Every Need
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              From working capital to equipment financing, we offer a range of products designed specifically for small businesses.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {loanTypes.map((loan, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                <span className="font-medium text-[#0A2540]">{loan.name}</span>
                <span className="text-amber-600 font-mono font-semibold text-sm">{loan.amount}</span>
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/products">
              <Button className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90" data-testid="view-all-products-btn">
                View All Products <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0A2540]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            Ready to Grow Your Business?
          </h2>
          <p className="mt-4 text-gray-300 max-w-2xl mx-auto">
            Join thousands of small businesses who have trusted Tzur Finances with their growth journey.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-amber-500 text-[#0A2540] hover:bg-amber-400 h-12 px-8 font-semibold" data-testid="cta-apply-btn">
                Start Your Application
              </Button>
            </Link>
            <Link to="/knowledge">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-12 px-8" data-testid="cta-learn-btn">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div>
              <img 
                src={LOGO_URL} 
                alt="Tzur Finances" 
                className="h-10 rounded-md bg-white/90 p-1" 
              />
              <p className="mt-4 text-sm">Partner for Growth</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Business Loans</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/products" className="hover:text-white transition-colors">Working Capital</Link></li>
                <li><Link to="/products" className="hover:text-white transition-colors">Invoice Discounting</Link></li>
                <li><Link to="/products" className="hover:text-white transition-colors">Merchant Cash Advance</Link></li>
                <li><Link to="/products" className="hover:text-white transition-colors">Term Loans</Link></li>
                <li><Link to="/products" className="hover:text-white transition-colors">Home Equity Loans</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Individual Loans</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/individual-loans" className="hover:text-white transition-colors">Sol Prop Loans</Link></li>
                <li><Link to="/individual-loans" className="hover:text-white transition-colors">Remittance Loans</Link></li>
              </ul>
              <h4 className="font-semibold text-white mb-4 mt-6">Device Finance</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/device-finance" className="hover:text-white transition-colors">Device Finance</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Wholesale Lending</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/wholesale-lending" className="hover:text-white transition-colors">Microfinance Wholesale Loans</Link></li>
              </ul>
              <h4 className="font-semibold text-white mb-4 mt-6">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/knowledge" className="hover:text-white transition-colors">Knowledge Portal</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>info@tzurfinances.com</li>
                <li>+263 711 692 385-7</li>
                <li className="leading-relaxed">Shop 5, Topics Building,<br />Cnr First and Nelson Mandela,<br />Harare</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 text-sm text-center">
            <p className="text-gray-400 mb-2">Tzur Finances is a registered microfinance institution</p>
            <p>© 2024 Tzur Finances. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
